// Copyright Epic Games, Inc. All Rights Reserved.

import React, { useEffect } from "react";
import { InstallationProgress } from "../../components/Installation/InstallationProgress";
import { InstallationTitle } from "../../components/Installation/InstallationTitle";
import { useDispatch } from "react-redux";
import {
  setInstallationErrorCode,
  setInstallationTaskInfo,
  startInstallationPulling,
  stopInstallationPolling,
} from "../../store/installation/actions";
import {
  useCancelInstallationDispatchAction,
  useInstallationErrorCode,
} from "../../store/installation/hooks";
import { ErrorPage } from "../../components/common/error/ErrorPage";
import { useParams } from "react-router";
import { InstallationBadConfigurationErrorCode } from "../../errors/installation.errorCodes";
import { EpicIcon } from "../../components/common/EpicIcon";
import { FullPageGrid } from "../../components/common/FullPageGrid";
import { useMainServiceAccessToken } from "../../hooks/mainServiceAccessToken/useMainServiceAccessToken";
import { useOnClose } from "../../hooks/global/useOnClose";
import { ErrorBoundary } from "../../components/common/error/ErrorBoundary";
import { UIUnexpectedErrorErrorCode } from "../../errors/UI.errorCodes";
import { Grid } from "@material-ui/core";

interface IInstallationProgressPageParams {
  groupId: string;
}

export const InstallationProgressPage = (): JSX.Element => {
  const dispatch = useDispatch();
  const { groupId } = useParams<IInstallationProgressPageParams>();
  const mainServiceAccessToken = useMainServiceAccessToken();
  const cancelInstallation = useCancelInstallationDispatchAction();
  const errorCode = useInstallationErrorCode();

  useOnClose(() => {
    cancelInstallation();
  });

  useEffect(() => {
    if (!groupId || !mainServiceAccessToken) {
      dispatch(setInstallationErrorCode(InstallationBadConfigurationErrorCode));
      return;
    }

    dispatch(setInstallationTaskInfo(groupId));
    dispatch(startInstallationPulling());

    return () => {
      dispatch(stopInstallationPolling());
    };
  }, [groupId]);

  if (errorCode) {
    return <ErrorPage errorCode={errorCode} onDismiss={cancelInstallation} />;
  }

  return (
    <ErrorBoundary
      errorCode={UIUnexpectedErrorErrorCode}
      onDismiss={cancelInstallation}
    >
      <FullPageGrid
        container
        direction="row"
        justify="center"
        alignItems="center"
        spacing={2}
      >
        <EpicIcon size="2.8rem" marginBottom="0px" />

        <Grid item style={{ paddingLeft: "40px" }}>
          <InstallationTitle />
          <InstallationProgress />
        </Grid>
      </FullPageGrid>
    </ErrorBoundary>
  );
};
